/**
 * Animation Utilities for The Networking Salon
 * Provides reusable animation patterns with accessibility support
 */

// Check if user prefers reduced motion
export const prefersReducedMotion = (): boolean => {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

// Animation duration constants (in ms)
export const ANIMATION_DURATION = {
  fast: 200,
  normal: 300,
  slow: 500,
  verySlow: 800,
} as const

// Easing functions
export const EASING = {
  easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
  easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
  spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
} as const

// Intersection Observer for scroll animations
export const createScrollObserver = (
  callback: (entry: IntersectionObserverEntry) => void,
  options?: IntersectionObserverInit,
) => {
  if (typeof window === 'undefined') return null

  // Respect reduced motion preference
  if (prefersReducedMotion()) {
    // Immediately show all scroll-animate elements
    document
      .querySelectorAll(
        '.scroll-animate, .scroll-animate-left, .scroll-animate-right, .scroll-animate-scale',
      )
      .forEach((el) => {
        el.classList.add('animate-in')
      })
    return null
  }

  const defaultOptions: IntersectionObserverInit = {
    threshold: 0.1,
    rootMargin: '0px 0px -50px 0px',
    ...options,
  }

  return new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        callback(entry)
      }
    })
  }, defaultOptions)
}

// Enhanced scroll observer that handles all animation classes
export const initScrollAnimations = () => {
  const observer = createScrollObserver((entry) => {
    entry.target.classList.add('animate-in')
  })

  if (observer) {
    document
      .querySelectorAll(
        '.scroll-animate, .scroll-animate-left, .scroll-animate-right, .scroll-animate-scale',
      )
      .forEach((el) => {
        observer.observe(el)
      })
  }

  return observer
}

// Scroll progress tracker
export const createScrollProgressTracker = (
  onProgress: (progress: number) => void,
) => {
  if (typeof window === 'undefined') return () => {}

  const handleScroll = () => {
    const scrollTop = window.scrollY
    const docHeight = document.documentElement.scrollHeight - window.innerHeight
    const progress =
      docHeight > 0 ? Math.min((scrollTop / docHeight) * 100, 100) : 0
    onProgress(progress)
  }

  window.addEventListener('scroll', handleScroll, { passive: true })
  handleScroll() // Initial call

  return () => window.removeEventListener('scroll', handleScroll)
}

// Stagger animation delays
export const getStaggerDelay = (
  index: number,
  baseDelay: number = 100,
): string => {
  return `${index * baseDelay}ms`
}

// Hover animation class generator
export const getHoverAnimation = (
  type: 'lift' | 'glow' | 'scale' = 'lift',
): string => {
  const animations = {
    lift: 'transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg',
    glow: 'transition-shadow duration-300 hover:shadow-xl',
    scale: 'transition-transform duration-300 hover:scale-105',
  }
  return animations[type]
}

// Fade in animation
export const fadeIn = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: EASING.easeOut },
}

// Slide in from side
export const slideInFromLeft = {
  initial: { opacity: 0, x: -50 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: 0.5, ease: EASING.easeOut },
}

export const slideInFromRight = {
  initial: { opacity: 0, x: 50 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: 0.5, ease: EASING.easeOut },
}

// Scale animation
export const scaleIn = {
  initial: { opacity: 0, scale: 0.9 },
  animate: { opacity: 1, scale: 1 },
  transition: { duration: 0.3, ease: EASING.spring },
}

// CSS class names for common animations
export const ANIMATION_CLASSES = {
  fadeIn: 'animate-fade-in',
  slideUp: 'animate-slide-up',
  slideDown: 'animate-slide-down',
  scaleIn: 'animate-scale-in',
  pulse: 'animate-pulse',
  spin: 'animate-spin',
} as const

// Utility to add animation class with reduced motion support
export const addAnimationClass = (
  element: HTMLElement,
  animationClass: string,
) => {
  if (prefersReducedMotion()) {
    element.classList.add('opacity-100')
    return
  }
  element.classList.add(animationClass)
}
